import React, { useMemo, useRef } from 'react'
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  HStack,
  VStack,
  Badge,
  IconButton,
  Button,
  Tooltip,
  useColorModeValue,
  VisuallyHidden,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { FiArrowUp, FiArrowDown, FiPower } from 'react-icons/fi'
import { formatDateTime } from '@/utils/formatUtils'
import {
  useRpcHealth,
  useUpdateRpcPriority,
  useUpdateRpcEndpointStatus,
  useUpdateRpcPriorityBulk,
} from '@/hooks'
import type { RpcHealthSnapshot, RpcEndpointHealth } from '@/types'
import { PageSkeleton } from '@/components/common/LoadingSkeleton'
import RpcGlobalOrderModal from '@/components/monitoring/RpcGlobalOrderModal'
import { formatChainName, getChainInfo } from '@/constants/chains'
import { ChainBadge } from '@/components/common/ChainBadge'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'

const formatChainTitleFromKey = (chainKey: string): string => {
  const normalized = chainKey.trim().toLowerCase()
  if (normalized.startsWith('solana-devnet')) {
    return 'Solana Devnet'
  }
  if (normalized.startsWith('solana-main')) {
    return 'Solana Mainnet'
  }
  if (normalized.startsWith('tron-nile')) {
    return 'Tron Nile'
  }
  if (normalized.startsWith('tron-main')) {
    return 'Tron Mainnet'
  }
  if (normalized.startsWith('eth-sepolia')) {
    return 'Ethereum Sepolia'
  }
  if (normalized.startsWith('eth-main')) {
    return 'Ethereum'
  }
  if (normalized.startsWith('base-sepolia')) {
    return 'Base Sepolia'
  }
  if (normalized.startsWith('base-main')) {
    return 'Base'
  }
  if (normalized.startsWith('bsc-testnet')) {
    return 'BNB Smart Chain Testnet'
  }
  if (normalized.startsWith('bsc-main')) {
    return 'BNB Smart Chain'
  }
  if (normalized.startsWith('polygon-amoy')) {
    return 'Polygon Amoy'
  }
  if (normalized.startsWith('polygon-main')) {
    return 'Polygon'
  }
  return chainKey.toUpperCase()
}

const resolveSnapshotNetwork = (
  snapshot: RpcHealthSnapshot,
  registryNetwork: 'mainnet' | 'testnet' | undefined
): 'mainnet' | 'testnet' => {
  if (registryNetwork) {
    return registryNetwork
  }
  const normalized = snapshot.chainKey.trim().toLowerCase()
  if (
    normalized.includes('sepolia') ||
    normalized.includes('testnet') ||
    normalized.includes('devnet') ||
    normalized.includes('amoy') ||
    normalized.includes('nile')
  ) {
    return 'testnet'
  }
  return 'mainnet'
}

const statusColorMap: Record<string, string> = {
  healthy: 'green',
  degraded: 'yellow',
  down: 'red',
  disabled: 'gray',
}

const providerColorMap: Record<string, string> = {
  alchemy: 'blue',
  helius: 'cyan',
  ankr: 'purple',
  public: 'teal',
}

const resolveProviderType = (providerType: unknown): string => {
  if (typeof providerType === 'string' && providerType.trim().length > 0) {
    const normalized = providerType.trim().toLowerCase()
    if (normalized === 'custom') {
      return 'public'
    }
    return normalized
  }
  return 'public'
}

const formatLatency = (latency?: number): string => {
  if (latency == null) {
    return '--'
  }
  if (latency >= 1000) {
    return `${(latency / 1000).toFixed(2)}s`
  }
  return `${latency}ms`
}

const formatTimestamp = (value?: number): string => {
  if (!value) {
    return '--'
  }
  return formatDateTime(value, { fallback: '--' })
}

const sortEndpoints = (endpoints: RpcEndpointHealth[]): RpcEndpointHealth[] => {
  return [...endpoints].sort((a, b) => a.priority - b.priority)
}

export const RpcMonitor: React.FC = () => {
  const { t } = useTranslation()
  const cardBg = useColorModeValue('white', 'gray.800')
  const tableBorder = useColorModeValue('gray.200', 'gray.700')

  const {
    data: snapshots = [],
    isLoading,
    isFetching,
    refetch,
  } = useRpcHealth(60_000)

  const updatePriority = useUpdateRpcPriority()
  const updateStatus = useUpdateRpcEndpointStatus()
  const updatePriorityBulk = useUpdateRpcPriorityBulk()

  const isMutating = updatePriority.isPending || updateStatus.isPending || updatePriorityBulk.isPending

  const globalOrderDisclosure = useDisclosure()
  const resetAllDisclosure = useDisclosure()
  const resetAllCancelRef = useRef<HTMLButtonElement | null>(null)

  const orderedSnapshots = useMemo(() => {
    return snapshots.map(snapshot => ({
      ...snapshot,
      endpoints: sortEndpoints(snapshot.endpoints),
    }))
  }, [snapshots])

  const handleReorder = (chainKey: string, endpointId: string, direction: 'up' | 'down') => {
    const snapshot = orderedSnapshots.find(item => item.chainKey === chainKey)
    if (!snapshot) {return}

    const endpoints = sortEndpoints(snapshot.endpoints)
    const index = endpoints.findIndex(endpoint => endpoint.id === endpointId)
    if (index === -1) {return}

    const swapIndex = direction === 'up' ? index - 1 : index + 1
    if (swapIndex < 0 || swapIndex >= endpoints.length) {return}

    const reordered = [...endpoints]
    const [removed] = reordered.splice(index, 1)
    reordered.splice(swapIndex, 0, removed)

    updatePriority.mutate({
      chainKey,
      endpointOrder: reordered.map(endpoint => endpoint.id),
    })
  }

  const handleReset = (chainKey: string) => {
    updatePriority.mutate({ chainKey, reset: true })
  }

  const handleToggle = (chainKey: string, endpointId: string, disabled: boolean) => {
    updateStatus.mutate({ chainKey, endpointId, disabled })
  }

  const handleGlobalOrderSubmit = (providerOrder: string[]) => {
    updatePriorityBulk.mutate(
      { mode: 'reorder', providerOrder },
      {
        onSuccess: () => {
          globalOrderDisclosure.onClose()
        },
      },
    )
  }

  const handleResetAllConfirm = () => {
    updatePriorityBulk.mutate(
      { mode: 'reset' },
      {
        onSuccess: () => {
          resetAllDisclosure.onClose()
        },
      },
    )
  }

  const renderChainHeader = (snapshot: RpcHealthSnapshot) => {
    const activeEndpoint = snapshot.endpoints.find(endpoint => endpoint.id === snapshot.activeEndpointId)
    const chainInfo = getChainInfo(snapshot.chainId)
    const title =
      chainInfo.numericChainId > 0
        ? formatChainName(snapshot.chainId, 'full')
        : formatChainTitleFromKey(snapshot.chainKey)
    const network = resolveSnapshotNetwork(
      snapshot,
      chainInfo.numericChainId > 0 &&
      (chainInfo.network === 'mainnet' || chainInfo.network === 'testnet')
        ? chainInfo.network
        : undefined
    )

    return (
      <HStack justify="space-between" align="center" spacing={4} wrap="wrap">
        <VStack align="start" spacing={1}>
          <ChainBadge
            chainId={snapshot.chainKey}
            chainCode={snapshot.chainKey}
            chainName={title}
            chainNumericId={snapshot.chainId}
            isTestnet={network === 'testnet'}
          />
          <HStack spacing={2}>
            <Text fontSize="sm" color="gray.500">
              {t('monitoring.rpc.chainLabel', { chainId: snapshot.chainId })}
            </Text>
          </HStack>
        </VStack>
        <HStack spacing={2}>
          {activeEndpoint && (
            <Badge colorScheme="green" variant="solid">
              {t('monitoring.rpc.activeEndpoint', { label: activeEndpoint.label })}
            </Badge>
          )}
          <Tooltip label={t('monitoring.rpc.resetTooltip')}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleReset(snapshot.chainKey)}
              isDisabled={isMutating}
            >
              {t('monitoring.rpc.resetOrder')}
            </Button>
          </Tooltip>
        </HStack>
      </HStack>
    )
  }

  const renderEndpointRow = (snapshot: RpcHealthSnapshot, endpoint: RpcEndpointHealth, index: number) => {
    const statusColor = statusColorMap[endpoint.status] ?? 'gray'
    const providerType = resolveProviderType(endpoint.providerType)
    const providerColor = providerColorMap[providerType] ?? 'gray'
    const isDisabled = endpoint.status === 'disabled'

    return (
      <Tr key={endpoint.id}>
        <Td minW="140px" pr={2}>
          <HStack spacing={2}>
            <Badge colorScheme={providerColor} variant="subtle">
              {providerType.toUpperCase()}
            </Badge>
            <Text fontWeight="medium" noOfLines={1}>
              {endpoint.label}
            </Text>
          </HStack>
        </Td>
        <Td width="72px" textAlign="center">
          <Text fontSize="sm" fontWeight="medium">
            {endpoint.priority}/{endpoint.defaultPriority}
          </Text>
        </Td>
        <Td>
          <Badge colorScheme={statusColor}>{endpoint.status}</Badge>
        </Td>
        <Td width="80px" textAlign="center">{formatLatency(endpoint.lastLatencyMs)}</Td>
        <Td width="100px" textAlign="center">{formatTimestamp(endpoint.lastSuccessAt)}</Td>
        <Td width="100px" textAlign="center">{formatTimestamp(endpoint.lastFailureAt)}</Td>
        <Td width="88px" textAlign="center">
          <Text fontSize="sm" fontWeight="medium">
            {endpoint.successCount}/{endpoint.failureCount}
          </Text>
        </Td>
        <Td width="80px" textAlign="right" whiteSpace="nowrap" pr={1}>
          <HStack spacing={1} justify="flex-end">
            <Tooltip label={t('monitoring.rpc.moveUp')}>
              <IconButton
                aria-label={t('monitoring.rpc.moveUp')}
                icon={<FiArrowUp />}
                size="sm"
                variant="ghost"
                isDisabled={index === 0 || isMutating}
                onClick={() => handleReorder(snapshot.chainKey, endpoint.id, 'up')}
              />
            </Tooltip>
            <Tooltip label={t('monitoring.rpc.moveDown')}>
              <IconButton
                aria-label={t('monitoring.rpc.moveDown')}
                icon={<FiArrowDown />}
                size="sm"
                variant="ghost"
                isDisabled={index === snapshot.endpoints.length - 1 || isMutating}
                onClick={() => handleReorder(snapshot.chainKey, endpoint.id, 'down')}
              />
            </Tooltip>
            <Tooltip label={isDisabled ? t('monitoring.rpc.enableEndpoint') : t('monitoring.rpc.disableEndpoint')}>
              <IconButton
                aria-label={isDisabled ? t('monitoring.rpc.enableEndpoint') : t('monitoring.rpc.disableEndpoint')}
                icon={<FiPower />}
                size="sm"
                variant={isDisabled ? 'solid' : 'ghost'}
                colorScheme={isDisabled ? 'green' : 'red'}
                onClick={() => handleToggle(snapshot.chainKey, endpoint.id, !isDisabled)}
                isDisabled={isMutating}
              />
            </Tooltip>
          </HStack>
        </Td>
      </Tr>
    )
  }

  if (isLoading) {
    return (
      <Box role="status" aria-busy="true">
        <PageSkeleton minHeight="260px" cardCount={2} />
        <VisuallyHidden>{t('common.loading')}</VisuallyHidden>
      </Box>
    )
  }

  return (
    <>
      <VStack align="stretch" spacing={6}>
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="md">{t('monitoring.rpc.title')}</Heading>
            <Text fontSize="sm" color="gray.500">{t('monitoring.rpc.subtitle')}</Text>
          </VStack>
          <HStack spacing={2}>
            <Tooltip label={t('monitoring.rpc.globalReorderTooltip')}>
              <Button
                size="sm"
                variant="outline"
                onClick={globalOrderDisclosure.onOpen}
                isDisabled={orderedSnapshots.length === 0 || isMutating}
              >
                {t('monitoring.rpc.globalReorder')}
              </Button>
            </Tooltip>
            <Tooltip label={t('monitoring.rpc.resetAllTooltip')}>
              <Button
                size="sm"
                colorScheme="red"
                variant="ghost"
                onClick={resetAllDisclosure.onOpen}
                isDisabled={orderedSnapshots.length === 0 || isMutating}
              >
                {t('monitoring.rpc.resetAll')}
              </Button>
            </Tooltip>
            <RefreshIconButton
              label={t('monitoring.rpc.manualRefresh')}
              onClick={() => refetch()}
              isLoading={isFetching || isMutating}
            />
          </HStack>
        </Flex>

        {orderedSnapshots.length === 0 ? (
          <Box textAlign="center" py={10} borderWidth="1px" borderRadius="lg" borderColor={tableBorder}>
            <Text color="gray.500">{t('monitoring.rpc.noData')}</Text>
          </Box>
        ) : (
          orderedSnapshots.map(snapshot => (
            <Card key={snapshot.chainKey} bg={cardBg} shadow="sm">
              <CardHeader>{renderChainHeader(snapshot)}</CardHeader>
              <CardBody>
              <TableContainer>
                <Table
                  variant="simple"
                  size="sm"
                  sx={{
                    'th, td': {
                      px: { base: 1, md: 1.5 },
                      py: { base: 1.5, md: 2 },
                    },
                    th: {
                      fontSize: 'sm',
                      color: 'gray.500',
                      textTransform: 'none',
                      letterSpacing: 'normal',
                    },
                  }}
                >
                  <Thead>
                    <Tr>
                      <Th>{t('monitoring.rpc.endpoint')}</Th>
                      <Th textAlign="center">{t('monitoring.rpc.priorityCombined')}</Th>
                      <Th>{t('monitoring.rpc.status')}</Th>
                      <Th textAlign="center">{t('monitoring.rpc.lastLatency')}</Th>
                      <Th textAlign="center">{t('monitoring.rpc.lastSuccess')}</Th>
                      <Th textAlign="center">{t('monitoring.rpc.lastFailure')}</Th>
                      <Th textAlign="center">{t('monitoring.rpc.successFailure')}</Th>
                      <Th>{t('monitoring.rpc.actions')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {snapshot.endpoints.map((endpoint, index) => renderEndpointRow(snapshot, endpoint, index))}
                  </Tbody>
                </Table>
              </TableContainer>
              </CardBody>
            </Card>
          ))
        )}
      </VStack>

      <RpcGlobalOrderModal
        isOpen={globalOrderDisclosure.isOpen}
        onClose={globalOrderDisclosure.onClose}
        snapshots={snapshots}
        isSubmitting={updatePriorityBulk.isPending}
        onSubmit={handleGlobalOrderSubmit}
      />

      <AlertDialog
        isOpen={resetAllDisclosure.isOpen}
        leastDestructiveRef={resetAllCancelRef}
        onClose={resetAllDisclosure.onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('monitoring.rpc.resetAllTitle')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t('monitoring.rpc.resetAllDescription')}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={resetAllCancelRef}
                onClick={resetAllDisclosure.onClose}
                isDisabled={updatePriorityBulk.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="red"
                onClick={handleResetAllConfirm}
                ml={3}
                isLoading={updatePriorityBulk.isPending}
                isDisabled={updatePriorityBulk.isPending}
              >
                {t('monitoring.rpc.resetAllConfirm')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}

export default RpcMonitor
